// Editor-gated multi-field correction modal for variations. Bypasses
// the valueImpact status-gate so an Approved or Rejected
// variation can have its name / description / value impact / effective
// date corrected for genuine data-entry mistakes — that's the entire
// point of the surface.
//
// Submit semantics: only fields whose value changed from the current
// variation are sent in the request body, matching the backend's
// "at least one new* field" refinement. The reason field is mandatory
// (≥10 chars) and feeds the audit row.

import { useMemo, useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import CurrencyInput from "../shared/CurrencyInput";
import { gstSuffix } from "../../../../utils/gstSuffix";
import { ApiError } from "../../../../services/api";
import type { CorrectVariationInput, Variation } from "../../../../types";

const MIN_REASON = 10;
const MAX_REASON = 500;

interface CorrectVariationModalProps {
  isOpen: boolean;
  onClose: () => void;
  variation: Variation | null;
  gstApplies: boolean | undefined;
  onConfirm: (body: CorrectVariationInput) => Promise<unknown>;
  isSubmitting?: boolean;
}

function valueImpactToRaw(v: number | null): string {
  if (v === null) return "";
  return String(v);
}

function parseValueImpact(raw: string): {
  ok: boolean;
  value: number | null;
} {
  const trimmed = raw.trim();
  if (
    trimmed === "" ||
    trimmed === "-" ||
    trimmed === "+" ||
    trimmed === "."
  ) {
    return { ok: false, value: null };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { ok: false, value: null };
  // Round to 2dp so the wire payload matches the backend monetaryAmount
  // invariant (≤2 decimal places).
  return { ok: true, value: Math.round(n * 100) / 100 };
}

export default function CorrectVariationModal({
  isOpen,
  onClose,
  variation,
  gstApplies,
  onConfirm,
  isSubmitting,
}: CorrectVariationModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [valueImpactRaw, setValueImpactRaw] = useState("");
  const [nonFinancial, setNonFinancial] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Pattern 0: seed all fields from the target variation when the modal opens
  // so a previous correction attempt does not persist across invocations.
  const seedKey = isOpen && variation ? variation.id : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setName(variation!.name);
    setDescription(variation!.description ?? "");
    setValueImpactRaw(valueImpactToRaw(variation!.valueImpact));
    setNonFinancial(variation!.valueImpact === null);
    setEffectiveDate(variation!.effectiveDate ?? "");
    setReason("");
    setError(null);
  }
  if (!isOpen && seededFor !== null) {
    setSeededFor(null);
  }

  const basis = gstSuffix(gstApplies);

  // Compute the sparse correction body — only fields that differ from the
  // current variation are sent, matching the backend's at-least-one-new
  // refinement.
  const body = useMemo<CorrectVariationInput | null>(() => {
    if (!variation) return null;
    const result: Partial<CorrectVariationInput> = {};

    const trimmedName = name.trim();
    if (trimmedName !== variation.name) {
      if (trimmedName === "") return null;
      result.newName = trimmedName;
    }

    const currentDesc = variation.description ?? "";
    if (description !== currentDesc) {
      result.newDescription = description === "" ? null : description;
    }

    let nextValueImpact: number | null;
    if (nonFinancial) {
      nextValueImpact = null;
    } else {
      const parsed = parseValueImpact(valueImpactRaw);
      if (!parsed.ok) return null;
      if (parsed.value === 0) return null;
      nextValueImpact = parsed.value;
    }
    if (nextValueImpact !== variation.valueImpact) {
      result.newValueImpact = nextValueImpact;
    }

    const currentDate = variation.effectiveDate ?? "";
    if (effectiveDate !== currentDate) {
      result.newEffectiveDate = effectiveDate === "" ? null : effectiveDate;
    }

    const changedKeys = Object.keys(result);
    if (changedKeys.length === 0) return null;

    return { ...result, reason: reason.trim() } as CorrectVariationInput;
  }, [variation, name, description, valueImpactRaw, nonFinancial, effectiveDate, reason]);

  const reasonValid =
    reason.trim().length >= MIN_REASON && reason.length <= MAX_REASON;
  const canSubmit = !isSubmitting && body !== null && reasonValid;

  const handleConfirm = async () => {
    if (!canSubmit || !body) return;
    setError(null);
    try {
      await onConfirm(body);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Failed to correct variation.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        variation ? `Correct ${variation.number}?` : "Correct variation"
      }
    >
      <div className="space-y-4" data-testid="correct-variation-modal">
        <Alert variant="warning">
          This action is logged to the audit trail. Only fields you change
          will be saved. Use this surface only for genuine data-entry
          repair — variations affecting an active SOW or contract value
          should otherwise be raised as a new variation.
        </Alert>
        <div>
          <label htmlFor="correct-variation-name" className="label">
            Name
          </label>
          <input
            id="correct-variation-name"
            data-testid="correct-variation-name"
            className="input"
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
        <div>
          <label htmlFor="correct-variation-description" className="label">
            Description
          </label>
          <textarea
            id="correct-variation-description"
            data-testid="correct-variation-description"
            className="input min-h-[60px]"
            value={description}
            maxLength={2000}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="correct-variation-value-impact" className="label">
              Value impact
            </label>
            <div className="relative w-full">
              <span
                aria-hidden="true"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
              >
                $
              </span>
              <CurrencyInput
                id="correct-variation-value-impact"
                value={valueImpactRaw}
                onChange={setValueImpactRaw}
                allowNegative
                allowDecimal
                ariaLabel="Value impact"
                disabled={isSubmitting || nonFinancial}
                placeholder="0"
                testId="correct-variation-value-impact"
              />
            </div>
            <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
              Up to 2 decimal places, {basis}. Use negative for descope.
            </p>
            <label className="mt-2 inline-flex items-center gap-2 text-body-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                data-testid="correct-variation-non-financial"
                checked={nonFinancial}
                onChange={(e) => setNonFinancial(e.target.checked)}
                disabled={isSubmitting}
              />
              Non-financial variation
            </label>
          </div>
          <div>
            <label htmlFor="correct-variation-effective-date" className="label">
              Effective date
            </label>
            <input
              type="date"
              id="correct-variation-effective-date"
              data-testid="correct-variation-effective-date"
              className="input"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              disabled={isSubmitting}
            />
            <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
              Optional for proposed variations.
            </p>
          </div>
        </div>
        <div>
          <label htmlFor="correct-variation-reason" className="label">
            Reason
          </label>
          <textarea
            id="correct-variation-reason"
            data-testid="correct-variation-reason"
            className="input min-h-[80px]"
            value={reason}
            maxLength={MAX_REASON}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSubmitting}
            required
          />
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            {reason.length}/{MAX_REASON} (minimum {MIN_REASON})
          </p>
        </div>
        {body === null && reason.trim().length >= MIN_REASON && (
          <Alert variant="info">
            Change at least one field above to save a correction.
          </Alert>
        )}
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={!canSubmit}
            data-testid="correct-variation-submit"
          >
            Save correction
          </Button>
        </div>
      </div>
    </Modal>
  );
}
